// ============================================================
// WoundsReadout - the big gold "12 / 15" and its bar.
//
// Lifted verbatim out of TargetPanel's body-map tab, where it was
// pinned to the bottom of the panel by `margin-top: auto` - which is
// what produced the chasm of empty space the redesign is closing. The
// arrangement itself is not the problem and is unchanged: numbers,
// CRITICAL / at-zero warnings, health bar, and the horde variant with
// magnitude and attacks-per-turn.
//
// EDITING (v7.11.0) is deliberately invisible at rest. This is a mainly
// visual panel and a permanent input box would read as a form, so:
//   · hover  → discreet − / + for a one-point nudge
//   · click  → the number becomes an input; Enter or blur commits,
//              Escape cancels
// Same "click the thing to edit the thing" idiom as double-click-to-
// rename in the roster chip bar.
//
// Distinct from CombatantVitals on purpose. That one is a glanceable
// strip for a chip or a header; this is the centrepiece of a tab.
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { healthBandLabel, healthColor, vitalsOf } from '../engine/healthRamp';
import { useWoundDisplay } from '../hooks/useWoundDisplay';
import clsx from '../utils/clsx';
import type { Target } from '../types';
import styles from '../styles/WoundsReadout.module.css';
import { crowdHitsText } from '../engine/bodyShape';

export interface WoundsReadoutProps {
  target: Target;
  /**
   * Commit a new current value - wounds, or magnitude for a horde. Omit
   * for a read-only readout.
   */
  onChange?: (value: number) => void;
  locked?: boolean;
  className?: string;
}

/** The bar's fill, 0-100: a negative or overflowing value pins to an end. */
function barPercent(shown: number, total: number): number {
  return total > 0 ? Math.max(0, Math.min(100, (Math.max(0, shown) / total) * 100)) : 0;
}

export default function WoundsReadout({ target, onChange, locked = false, className }: WoundsReadoutProps) {
  const { shown, total, isHorde } = vitalsOf(target);
  const { showNumbers } = useWoundDisplay();
  const [editing, setEditing] = useState(false);
  // YOU CANNOT EDIT WHAT YOU CANNOT SEE. Stepping the value would print
  // it back, and the steppers themselves would let a player count.
  const editable = !!onChange && !locked && showNumbers;
  const noun = isHorde ? 'magnitude' : 'wounds';
  const colour = healthColor(shown, total);
  // Wounds go negative (critical severity); magnitude does not.
  const commit = (v: number) => onChange?.(Math.max(isHorde ? 0 : -99, Math.min(total, v)));
  const stepper = editable && !editing;
  return (
    <div className={clsx(styles.block, className)} data-testid="wounds-readout">
      {isHorde && <div className={styles.hordeLabel}>HORDE</div>}
      <div className={styles.valueRow}>
        {stepper && <StepButton label={`Decrease ${noun}`} onStep={() => commit(shown - 1)} glyph="−" />}
        <span className={styles.value} style={{ color: colour }}>
          {isHorde && '⚔ '}
          {showNumbers ? (
            <>
              <CurrentValue {...{ shown, colour, noun, editable, editing, setEditing, commit }} /> / {total}
            </>
          ) : (
            healthBandLabel(shown, total)
          )}
        </span>
        {stepper && <StepButton label={`Increase ${noun}`} onStep={() => commit(shown + 1)} glyph="+" />}
      </div>
      <HealthNotes shown={shown} isHorde={isHorde} showNumbers={showNumbers} />
      <div className={styles.bar} aria-hidden="true">
        <div className={styles.barFill} style={{ width: `${barPercent(shown, total)}%`, background: colour }} />
      </div>
    </div>
  );
}

/** A hover-only one-point nudge; out of the tab order (the number itself is the control). */
function StepButton({ label, onStep, glyph }: { label: string; onStep: () => void; glyph: string }) {
  return (
    <button
      className={styles.step}
      aria-label={label}
      onClick={onStep}
      tabIndex={-1}
      type="button"
      data-ghost-hide="true"
    >
      {glyph}
    </button>
  );
}

interface CurrentValueProps {
  shown: number;
  colour: string;
  noun: string;
  editable: boolean;
  editing: boolean;
  setEditing: (editing: boolean) => void;
  /** Commit a value (clamped by the readout). */
  commit: (value: number) => void;
}

/**
 * The current number: plain, a click-to-edit trigger, or - while editing -
 * an input that commits on Enter or blur and cancels on Escape.
 */
function CurrentValue({ shown, colour, noun, editable, editing, setEditing, commit }: CurrentValueProps) {
  if (editing) return <ValueInput shown={shown} noun={noun} onDone={() => setEditing(false)} commit={commit} />;
  if (!editable) return <span style={{ color: colour }}>{shown}</span>;
  return (
    <button
      className={styles.editTrigger}
      style={{ color: colour }}
      aria-label={`Edit current ${noun}`}
      title="Click to edit"
      onClick={() => setEditing(true)}
    >
      {shown}
    </button>
  );
}

interface ValueInputProps {
  shown: number;
  noun: string;
  onDone: () => void;
  commit: (value: number) => void;
}

/** The edit field, opened on the current value and selected. */
function ValueInput({ shown, noun, onDone, commit }: ValueInputProps) {
  const [draft, setDraft] = useState(() => String(shown));
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.select();
  }, []);
  const finish = () => {
    const parsed = parseInt(draft, 10);
    // An emptied or unparseable field cancels rather than zeroing someone.
    if (!Number.isNaN(parsed)) commit(parsed);
    onDone();
  };
  return (
    <input
      ref={inputRef}
      className={styles.input}
      type="number"
      value={draft}
      aria-label={`Current ${noun}`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish();
        if (e.key !== 'Escape') return;
        // Stop here: the panel's own Escape would close it, and the blur commit the edit just cancelled.
        e.stopPropagation();
        onDone();
      }}
    />
  );
}

/**
 * A horde's extra hits on each attack and its end; a person's CRITICAL severity or
 * the warning at zero. The severity IS the wound count past zero, so
 * printing it beside a band would hand back the number the band hides.
 */
function HealthNotes({ shown, isHorde, showNumbers }: { shown: number; isHorde: boolean; showNumbers: boolean }) {
  if (isHorde) {
    return (
      <>
        <div className={styles.detail}>Magnitude ({crowdHitsText(shown, true)})</div>
        {shown <= 0 && <div className={styles.destroyed}>DESTROYED / SCATTERED</div>}
      </>
    );
  }
  return (
    <>
      {shown < 0 && (
        <div className={styles.critical}>{showNumbers ? `CRITICAL (Severity ${Math.abs(shown)})` : 'CRITICAL'}</div>
      )}
      {shown === 0 && showNumbers && <div className={styles.atZero}>At 0 - next hit is Critical</div>}
    </>
  );
}
